import { MAP_COORDINATE_OFFSET } from '@/constants/mapZoom';
import { CityFormData, GeoJSONPoint, Coordinates } from '@/types/city';
import { debug, warn } from '@/utils/logger';

/**
 * Extracts coordinates from a city object in various possible formats.
 * Handles multiple coordinate formats that may come from different API responses.
 * 
 * Priority order (most relevant first):
 * 1. google_places_data.location - Usually the most relevant for tourism/business centers
 * 2. geo_position - Legacy format
 * 3. geoposition.coordinates - Geographic/admin center (may be less relevant for tourism)
 * 
 * Note: Coordinates are adjusted to account for visual centering when maps are displayed
 * in tall containers. See MAP_COORDINATE_OFFSET for details.
 * 
 * @param city - City object that may contain coordinates in different formats:
 *   - city.google_places_data.location (with latitude and longitude) - PREFERRED for tourism relevance
 *   - city.geo_position (with lat and long)
 *   - city.geoposition.coordinates (array format [lng, lat]) - May be administrative center
 * @returns Object with lat and lng (adjusted for visual centering), or null if coordinates cannot be extracted
 */
export const getCityCoordinates = (city: CityFormData | null | undefined): { lat: number; lng: number } | null => {
    if (!city) return null;
    
    let lat: number | undefined;
    let lng: number | undefined;
    let coordinateSource = 'none';
    
    // Priority 1: Google Places data (usually most relevant for tourism/business centers)
    // This often points to the city's main business/tourism area rather than geographic center
    if (city.google_places_data?.location) {
        // Handle MongoDB $numberDouble format
        const latValue = city.google_places_data.location.latitude;
        const lngValue = city.google_places_data.location.longitude;
        
        lat = typeof latValue === 'object' && latValue !== null && '$numberDouble' in latValue
            ? Number((latValue as { $numberDouble: string }).$numberDouble)
            : (typeof latValue === 'number' ? latValue : 0);
        
        lng = typeof lngValue === 'object' && lngValue !== null && '$numberDouble' in lngValue
            ? Number((lngValue as { $numberDouble: string }).$numberDouble)
            : (typeof lngValue === 'number' ? lngValue : 0);
        
        coordinateSource = 'google_places_data';
    } 
    // Priority 2: Legacy geo_position format
    else if (city.geo_position) {
        lat = city.geo_position.lat;
        lng = city.geo_position.long;
        coordinateSource = 'geo_position';
    } 
    // Priority 3: Coordinates object (from City model serialization)
    // Handle { coordinates: { latitude, longitude } } format
    // Also handles City model instances where coordinates is a Coordinates object
    else if ((city as Record<string, unknown>).coordinates && typeof (city as Record<string, unknown>).coordinates === 'object') {
        const coords = (city as Record<string, unknown>).coordinates as { latitude?: number; longitude?: number };
        // Handle both plain objects and Coordinates model instances
        // Coordinates model instances have latitude and longitude as direct properties
        const coordLat = coords.latitude;
        const coordLng = coords.longitude;
        
        if (typeof coordLat === 'number' && typeof coordLng === 'number' && 
            !(coordLat === 0 && coordLng === 0)) { // Exclude default (0,0) coordinates
            lat = coordLat;
            lng = coordLng;
            coordinateSource = 'coordinates';
        }
    }
    // Priority 4: GeoJSON geoposition (may be administrative/geographic center)
    // This is checked last as it may point to less relevant locations (e.g., city hall)
    else if (city.geoposition?.coordinates?.length === 2) {
        // GeoJSON format: [longitude, latitude]
        lng = city.geoposition.coordinates[0];
        lat = city.geoposition.coordinates[1];
        coordinateSource = 'geoposition';
    }
    
    // Validate coordinates are within valid ranges
    if (lat !== undefined && lng !== undefined) {
        // Latitude must be between -90 and 90
        // Longitude must be between -180 and 180
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            // Apply coordinate offset to compensate for visual centering in tall map containers
            // See MAP_COORDINATE_OFFSET documentation for explanation
            const adjustedLat = lat + MAP_COORDINATE_OFFSET.LATITUDE_SOUTH;
            const adjustedLng = lng + MAP_COORDINATE_OFFSET.LONGITUDE;
            
            debug(`[getCityCoordinates] Using ${coordinateSource} coordinates:`, { 
                original: { lat, lng }, 
                adjusted: { lat: adjustedLat, lng: adjustedLng },
                offset: MAP_COORDINATE_OFFSET.LATITUDE_SOUTH,
                cityName: city.city || city.name 
            });
            
            return { lat: adjustedLat, lng: adjustedLng };
        } else {
            warn('Invalid city coordinates:', { lat, lng, coordinateSource, city });
        }
    }
    
    return null;
};

/**
 * Extracts coordinates from a PrimaryDestination object.
 * Returns adjusted coordinates (with viewport offset) when geoposition is present,
 * otherwise returns null so callers can fall back to city-based centering.
 *
 * Accepts a structurally compatible subset so it works with both the strict
 * PrimaryDestination type and the looser RawItineraryPost.primary_destination shape.
 *
 * @param dest - Object that may have a geoposition GeoJSON Point
 * @returns Object with lat and lng (adjusted for visual centering), or null
 */
export const getPrimaryDestinationCoordinates = (
  dest: { geoposition?: { type?: string; coordinates?: unknown } | null } | null | undefined
): { lat: number; lng: number } | null => {
  const coords = dest?.geoposition?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lng = coords[0];
  const lat = coords[1];
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return {
    lat: lat + MAP_COORDINATE_OFFSET.LATITUDE_SOUTH,
    lng: lng + MAP_COORDINATE_OFFSET.LONGITUDE,
  };
};

/**
 * Extracts raw coordinates from a PrimaryDestination object for use with API calls.
 * Returns the raw GeoJSON point WITHOUT the viewport display offset, so the backend
 * receives the true geographic centroid of the destination.
 *
 * Use this when sending coordinates to the itinerary-prediction API (predictNextSlot).
 * Use getPrimaryDestinationCoordinates (with offset) for map display centering.
 *
 * @param dest - Object that may have a geoposition GeoJSON Point
 * @returns Object with lat and lng (no offset), or null
 */
export const getPrimaryDestinationCoordinatesRaw = (
  dest: { geoposition?: { type?: string; coordinates?: unknown } | null } | null | undefined
): { lat: number; lng: number } | null => {
  const coords = dest?.geoposition?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lng = coords[0];
  const lat = coords[1];
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

/**
 * Applies viewport offset to coordinates for visual centering in tall map containers.
 * 
 * This is used for default locations (like New York) and any coordinates that need
 * adjustment for tall maps where the top is above the viewport.
 * 
 * @param coordinates - Object with lat and lng
 * @returns Adjusted coordinates with viewport offset applied
 */
export const applyViewportOffset = (coordinates: { lat: number; lng: number }): { lat: number; lng: number } => {
    return {
        lat: coordinates.lat + MAP_COORDINATE_OFFSET.LATITUDE_SOUTH,
        lng: coordinates.lng + MAP_COORDINATE_OFFSET.LONGITUDE,
    };
};

/**
 * Converts coordinates to GeoJSON Point format (industry standard).
 * GeoJSON uses [longitude, latitude] order.
 * 
 * This is the PREFERRED format for storing coordinates in the database.
 * 
 * @param coords - Coordinates in { lat, lng } format
 * @returns GeoJSON Point or null if invalid
 * 
 * @example
 * ```typescript
 * convertToGeoJSON({ lat: 40.7128, lng: -74.0060 })
 * // { type: 'Point', coordinates: [-74.0060, 40.7128] }
 * ```
 */
export function convertToGeoJSON(coords: Coordinates | null | undefined): GeoJSONPoint | null {
    if (!coords) return null;
    
    const { lat, lng } = coords;
    
    // Validate coordinates
    if (typeof lat !== 'number' || typeof lng !== 'number') {
        return null;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
    }
    
    // GeoJSON format: [longitude, latitude]
    return {
        type: 'Point',
        coordinates: [lng, lat],
    };
}

/**
 * Converts GeoJSON Point to standard coordinate format.
 * 
 * @param geojson - GeoJSON Point with coordinates [lng, lat]
 * @returns Coordinates in { lat, lng } format or null if invalid
 * 
 * @example
 * ```typescript
 * convertFromGeoJSON({ type: 'Point', coordinates: [-74.0060, 40.7128] })
 * // { lat: 40.7128, lng: -74.0060 }
 * ```
 */
export function convertFromGeoJSON(geojson: GeoJSONPoint | null | undefined): Coordinates | null {
    if (!geojson || geojson.type !== 'Point') {
        return null;
    }
    
    const [lng, lat] = geojson.coordinates;
    
    // Validate coordinates
    if (typeof lat !== 'number' || typeof lng !== 'number') {
        return null;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
    }
    
    return { lat, lng };
}

/**
 * Normalizes coordinates from any format to standard { lat, lng } format.
 * Handles all three coordinate formats used in the codebase.
 * 
 * Priority order:
 * 1. google_places_data.location (most relevant for tourism)
 * 2. geo_position (legacy format)
 * 3. geoposition (GeoJSON format)
 * 
 * @param city - City object with coordinates in any format
 * @returns Normalized coordinates or null if not found
 * 
 * @example
 * ```typescript
 * normalizeCoordinates({
 *   google_places_data: { location: { latitude: 40.7128, longitude: -74.0060 } }
 * })
 * // { lat: 40.7128, lng: -74.0060 }
 * ```
 */
export function normalizeCoordinates(city: CityFormData | null | undefined): Coordinates | null {
    if (!city) return null;
    
    let lat: number | undefined;
    let lng: number | undefined;
    
    // Priority 1: Google Places data
    if (city.google_places_data?.location) {
        let rawLat = city.google_places_data.location.latitude;
        let rawLng = city.google_places_data.location.longitude;
        
        // Handle MongoDB $numberDouble format
        if (typeof rawLat === 'object' && rawLat !== null && '$numberDouble' in rawLat) {
            rawLat = Number((rawLat as { $numberDouble: string }).$numberDouble);
        }
        if (typeof rawLng === 'object' && rawLng !== null && '$numberDouble' in rawLng) {
            rawLng = Number((rawLng as { $numberDouble: string }).$numberDouble);
        }
        
        if (typeof rawLat === 'number' && typeof rawLng === 'number') {
            lat = rawLat;
            lng = rawLng;
        }
    }
    
    // Priority 2: Legacy geo_position format
    if ((lat === undefined || lng === undefined) && city.geo_position) {
        lat = city.geo_position.lat;
        lng = city.geo_position.long;
    }
    
    // Priority 3: GeoJSON format
    if ((lat === undefined || lng === undefined) && city.geoposition?.coordinates?.length === 2) {
        // GeoJSON: [longitude, latitude]
        lng = city.geoposition.coordinates[0];
        lat = city.geoposition.coordinates[1];
    }
    
    // Validate and return
    if (lat !== undefined && lng !== undefined) {
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { lat, lng };
        }
    }
    
    return null;
}
